import { z } from "zod";
import { AutoCompleteField, FormBase, FormBaseProps, FormState, FormSubmitCallback, InputField, SelectField } from "ui/Form";
import { useEffect, useMemo, useState } from "react";
import { FieldProps } from "react-final-form";
import { genders, insuranceCompanies } from "ui/constants";
import { canBeAssignedStudent } from "ft-shared-graphql";

const STUDENT_FORM_FIELDS: FieldProps<string, any>[] = [
  {
    name: "firstName",
    label: "First Name",
    component: InputField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "middleName",
    label: "Middle Name",
    component: InputField,
    required: false,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "lastName",
    label: "Last Name",
    component: InputField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "dob",
    label: "Date of Birth",
    type: 'date',
    shrink: false,
    component: InputField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "postalCode",
    label: "Zip Code",
    component: InputField,
    required: true,
    fullWidth: true,
    grid: {
      xs: 6,
    },
  },
  {
    name: "parentPhone",
    label: "Parent phone",
    component: InputField,
    fullWidth: true,
    type: 'tel',
    grid: {
      xs: 6,
    },
  },
  {
    name: "email",
    label: "Child Email",
    component: InputField,
    fullWidth: true,
    grid: {
      xs: 12
    }
  },
  {
    name: "parentEmail",
    label: "Parent email",
    component: InputField,
    fullWidth: true,
    grid: {
      xs: 12,
    },
  },
  {
    name: "personIds",
    label: "Assigned Staff",
    component: AutoCompleteField,
    fullWidth: true,
    grid: { xs: 12 }
  }
];

export type CustomField = {
  id: string,
  type?: string
  name: string,
  value?: string
}

const customFieldsSchema = z.record(z.string(), z.object({
  id: z.string().nonempty(),
  value: z.string().nullish()
})).nullable().nullish();
// const zipcodeSchema = 
const studentEditSchema = z.object({
  firstName: z.string().trim().nonempty().max(255),
  middleName: z.string().trim().max(255).nullish(),
  lastName: z.string().trim().nonempty().max(255),
  dob: z.string().trim().max(255).nullish(),
  postalCode: z.string().trim().max(10),
  parentPhone: z.string().nullish(),
  parentEmail: z.string().email().nullish(),
  email: z.string().email().nullish(),
  personIds: z.array(z.string()).optional(),
  customFields: customFieldsSchema
});

export type StudentFormCustomFieldsSchema = z.infer<typeof customFieldsSchema>;
export type StudentFormSchema = z.infer<typeof studentEditSchema>;

function createCustomFields(columns: CustomField[]): FieldProps<string, any>[] {

  // Hidden ID
  const nextColumns: FieldProps<string, any>[] = [];
  columns.forEach(field => {
    nextColumns.push({
      name: `customFields.${field.id}[id]`,
      type: "hidden",
      value: field.id,
      required: true,
    });
    if (field.type == "Text") {
      nextColumns.push({
        name: `customFields.${field.id}[value]`,
        label: field.name,
        component: InputField,
        // required: true,
        fullWidth: true,
        grid: {
          xs: 6,
        }
      })
    } else if (field.type == "Select") {
      nextColumns.push({
        name: `customFields.${field.id}[value]`,
        label: field.name,
        component: SelectField,
        // required: true,
        fullWidth: true,
        options: field.name === "Health Insurance" ? insuranceCompanies : genders,
        grid: {
          xs: 6,
        }
      })
    }
  });

  return nextColumns;
}


export type StudentFormProps = {
  parentEmailNeeded: boolean,
  customFields: CustomField[],
  onSubmit: (values: StudentFormSchema) => Promise<ReturnType<FormSubmitCallback>>
  roles: string[],
  people: {
    id: string,
    label: string
  }[]
  onCancel: () => void
} & Pick<FormBaseProps<StudentFormSchema>, "initialValues" | "readonly">

export default function StudentForm({ parentEmailNeeded, customFields, initialValues, roles, people, readonly, onSubmit, onCancel }: StudentFormProps) {
  const [formState, setFormState] = useState<FormState>({
    fields: STUDENT_FORM_FIELDS
  });
  //CONDITIONALLY MARK THE PARENT EMAIL FIELD AS REQUIRED, IF THEY NEED PARENTAL CONSENT
  useEffect(() => {
    let fields = [
      ...STUDENT_FORM_FIELDS.slice(0, 6),
      ...createCustomFields(customFields),
      ...STUDENT_FORM_FIELDS.slice(6)]
    fields = fields
      .filter(field => field.name !== "personIds" || !canBeAssignedStudent(roles))
      .map(field => {
        if (field.name === "parentEmail" && parentEmailNeeded) {
          return {
            ...field,
            required: true
          }
        } else if (field.name == "personIds") {
          return {
            ...field,
            options: people,
            required: canBeAssignedStudent(roles) ? false : true

          }
        }
        return field
      })
    setFormState(prevState => ({
      ...prevState,
      fields
    }));
  }, [customFields, people]);
  const validationSchema = useMemo(() =>
    studentEditSchema.superRefine((values, ctx) => {
      if (!canBeAssignedStudent(roles) && !values.personIds?.length) {
        ctx.addIssue({
          code: "custom",
          path: ["personIds"],
          message: "Please select at least one person"
        });
      }
    }),
    [roles]
  );
  const EditForm = FormBase<StudentFormSchema>({
    fields: formState.fields,
    initialValues: initialValues ?? {},
    validationSchema: validationSchema,
    buttonSubmitLabel: "Save",
    buttonCancelLabel: "Cancel",
    readonly,
    onSubmit: onSubmit,
    onCancel: onCancel
  })

  return EditForm;
}